import {
  Avatar,
  Breadcrumb,
  Button,
  Flex,
  Image,
  Layout,
  Menu,
  Space,
} from "antd";
import { ReactNode, useEffect, useState } from "react";
import {
  FileSearchOutlined,
  HomeOutlined,
  MenuOutlined,
  ProjectOutlined,
  SettingOutlined,
  ToolOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useFeatureFlag } from "eligibility/hooks/useFeatureFlag";

import "./SideNavPageLayout.scss";
import { Logo, OrganizationSelector } from "shared/components";
import { Content, Footer, Header } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import { UserMenu } from "dashboard/components";
import { SearchBox } from "search/components";
import { isEmpty } from "lodash";
import { useNavigate, useLocation } from "react-router-dom";

import { useHasPermissions } from "auth/hooks";
import { buildAdminPath, buildDashboardPath, buildOrganizationSectionPath } from "shared/utils/paths";
import { useBoundStore } from "store";
import { NotificationBell } from "notifications/components";

type Props = {
  title?: string;
  defaultSelectedMenuItemKey?: string;
  children?: ReactNode;
};

export const SideNavPageLayout = ({
  title = "Dashboard",
  children,
}: Props) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const selected = ["admin", "settings", "eligibility", "projects", "dashboard"].find(key => pathSegments.includes(key)) ?? "dashboard";
  const { hasPermissions } = useHasPermissions();
  const { currentOrganization, user, tenant } = useBoundStore();
  const eligibilityEnabled = useFeatureFlag("eligibility_checker");

  const isTenantAdmin = user?.role === "owner" || user?.role === "admin";

  let menuItems = [
    {
      key: "dashboard",
      icon: <HomeOutlined />,
      label: "Dashboard",
      title: "Dashboard",
    },
  ];

  if (hasPermissions(["read:projects"])) {
    menuItems.push({
      key: "projects",
      icon: <ProjectOutlined />,
      label: "Projects",
      title: "Projects",
    });
  }

  if (eligibilityEnabled && hasPermissions(["read:eligibility_checks"])) {
    menuItems.push({
      key: "eligibility",
      icon: <FileSearchOutlined />,
      label: "Eligibility Checker",
      title: "Eligibility Checker",
    });
  }

  if (!!currentOrganization && hasPermissions(["update:organizations"])) {
    menuItems.push({
      key: "settings",
      icon: <SettingOutlined />,
      label: "Organization Settings",
      title: "Organization Settings",
    });
  }

  if (isTenantAdmin) {
    menuItems.push({
      key: "admin",
      icon: <ToolOutlined />,
      label: "Admin Settings",
      title: "Admin Settings",
    });
  }

  const onMenuSelectHandler = (e: any) => {
    const key = e?.key ?? "";
    const keyLower = key.toLowerCase();

    let link: string;
    if (keyLower === "dashboard") {
      link = buildDashboardPath(currentOrganization);
    } else if (keyLower === "admin") {
      link = buildAdminPath(tenant?.id);
    } else {
      link = buildOrganizationSectionPath(currentOrganization, keyLower);
    }

    navigate(link);
  };

  return (
    <>
      <Layout className='side-nav-page-layout-container'>
        <Sider
          width={300}
          className='side-nav-page-layout-sidebar'
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme='light'
        >
          <div className='side-nav-page-layout-logo-container'>
            <Logo className='side-nav-page-layout-logo' collapsed={collapsed} />
          </div>
          <OrganizationSelector collapsed={collapsed} />
          <Menu
            theme='light'
            mode='inline'
            selectedKeys={[selected]}
            items={menuItems}
            onSelect={onMenuSelectHandler}
          />
        </Sider>
        <Layout className='side-nav-page-layout-top-bar-container'>
          <Header>
            <Button
              type='text'
              icon={collapsed ? <MenuOutlined /> : <MenuOutlined />}
              onClick={() => setCollapsed(!collapsed)}
            />
            <Breadcrumb
              className='side-nav-page-layout-breadcrumb'
              items={[
                {
                  title: title,
                },
              ]}
            />
            <Flex
              className='side-nav-page-layout-right-menu-container'
              justify='flex-end'
              align='center'
              gap={16}
            >
              <div style={{ width: "30%" }}>
                <SearchBox />
              </div>
              <NotificationBell />
              <div className='side-nav-page-layout-user-menu-container'>
                <Space>
                  {isEmpty(user?.picture) ? (
                    <Avatar
                      className='side-nav-page-layout-menu-avatar'
                      size={36}
                      icon={<UserOutlined />}
                      shape='circle'
                    />
                  ) : (
                    <Image
                      className='side-nav-page-layout-menu-profile-picture'
                      src={user?.picture}
                      preview={false}
                    />
                  )}
                  <UserMenu />
                </Space>
              </div>
            </Flex>
          </Header>
          <Content className='side-nav-page-layout-content-container'>
            {children}
          </Content>
        </Layout>
      </Layout>
      <Footer
        style={{
          backgroundColor: "#000000",
          color: "#ffffff",
          padding: "16px 24px",
        }}
      >
        <Flex justify='center' align='center' gap={24} wrap='wrap'>
          <span>Copyright © 2026 TracPro Technologies, LLC - All Rights Reserved</span>
          <span style={{ color: "#555" }}>|</span>
          <a
            href='https://tracprotech.com/privacy-policy'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: "#ffffff" }}
          >
            Privacy Policy
          </a>
          <span style={{ color: "#555" }}>|</span>
          <a
            href='https://tracprotech.com/terms-of-service'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: "#ffffff" }}
          >
            Terms and Conditions
          </a>
        </Flex>
      </Footer>
    </>
  );
};
